import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoCheck } from "react-icons/go";
import { MdUploadFile } from "react-icons/md";
import { IoIosArrowRoundForward } from "react-icons/io";
import api from "../../services/Api";
import { useSelector } from "react-redux";
import Editor from "../../components/CKEditor";
import toast from "react-hot-toast";
import Title from "../Title";

const resizeObserverLoopErr =
  "ResizeObserver loop completed with undelivered notifications.";

window.addEventListener("error", (e) => {
  if (
    e.message === resizeObserverLoopErr ||
    (e.message && e.message.includes("ResizeObserver"))
  ) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const originalError = console.error;
console.error = (...args) => {
  if (
    args[0] &&
    typeof args[0] === "string" &&
    args[0].includes("ResizeObserver")
  ) {
    return;
  }
  originalError.apply(console, args);
};

const PostBlogs = () => {
  const userData = useSelector((state) => state.auth.userData);
  const userEmail = userData?.email;
  const navigate = useNavigate();

  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await api.get("/industryListing/industries");
        if (response.data) {
          setIndustries(response.data.industries);
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
      }
    };

    fetchIndustries();
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [currentTag, setCurrentTag] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [post, setPost] = useState({
    image: null,
    title: "",
    description: "",
    body: "",
    tag: "",
    featured: false,
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!post.image) newErrors.image = "Image is required";
    if (!post.title) newErrors.title = "Title is required";
    if (!post.description) newErrors.description = "Description is required";
    if (!post.body) newErrors.body = "Body is required";
    if (selectedTags.length <= 0)
      newErrors.tags = "At least one tag is required";
    if (!selectedCategory) newErrors.category = "Category is required";

    return newErrors;
  };

  const handleCategoryChange = (event) => {
    const category = event.target.value;
    setSelectedCategory(category);

    const industry = industries.find((ind) => ind.name === category);

    if (industry && industry.tags) {
      setAvailableTags(industry.tags);
    } else {
      console.log("No tags found");
      setAvailableTags([]);
    }
    setSelectedTags([]);
  };

  const removeTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter((tag) => tag !== tagToRemove));
  };

  const handleInputChange = (field) => (event) => {
    const { value, checked } = event.target;

    if (field === "featured") {
      setPost((prevPost) => ({ ...prevPost, [field]: checked }));
    } else {
      setPost((prevPost) => ({ ...prevPost, [field]: value }));
    }
  };

  const handleEditorContentChange = (content) => {
    setPost((prevPost) => ({ ...prevPost, body: content }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      const maxSize = 5 * 1024 * 1024; 
      
      if (file.size > maxSize) {
        setErrors({ 
          ...errors, 
          image: "File size exceeds 5MB limit. Please choose a smaller image." 
        });
        event.target.value = "";
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setErrors({ 
          ...errors, 
          image: "Only JPG, PNG, and GIF images are allowed." 
        });
        event.target.value = "";
        return;
      }
      
      setPost({ ...post, image: file });
      setErrors({ ...errors, image: null });
    } else {
      setErrors({ ...errors, image: "Please upload a valid image." });
    }
  };
  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitted(true);

    try {
      let imageUrl = "";

      if (post.image) {
        const imageFormData = new FormData();
        imageFormData.append("file", post.image);

        const imageResponse = await api.post(
          `/upload/file/${userEmail}`,
          imageFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (imageResponse.data && imageResponse.data.url) {
          imageUrl = imageResponse.data.url;
        } else {
          throw new Error("Image upload failed");
        }
      }

      const payload = {
        adminId: userData?.sub,
        title: post?.title,
        content: post?.body,
        description: post?.description,
        category: selectedCategory,
        isFeatured: post?.featured,
        imageUrl: imageUrl,
        tags: selectedTags,
      };

      const response = await api.post("/blog", payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 201) {
        setShowModal(true);
      } else {
        toast("Unexpected response from the server.");
      }
    } catch (error) {
      console.error("Error creating blog post:", error.response?.data || error);
      toast("Error creating blog post. Please try again.");
    } finally {
      setSubmitted(false);
      window.scrollTo(0, 0);
    }
  };

  const closeModal = () => setShowModal(false);
  const viewBlogs = () => {
    navigate("manage");
  };

  return (
    <div className="p-8 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="mt-4 mb-2">
        <Title title="Post a Blog" />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="rounded">
          <div className="p-8 bg-white rounded-xl">
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="flex-1 w-2/3">
                <div className="border border-gray-300 rounded-xl">
                  <div className="p-5 pb-4">
                    <div
                      className={`lg:w-7/12 flex items-center justify-center border-2 border-dashed ${
                        errors?.image ? "border-red-500" : "border-gray-300"
                      } rounded-2xl mt-1 mx-auto`}
                    >
                      <div className="flex flex-col items-center gap-2 px-5 py-8">
                        <div className="flex items-center justify-center w-10 h-10 text-center bg-gray-100 rounded-full">
                          {post.image ? (
                            <GoCheck className="text-xl text-green-500" />
                          ) : (
                            <MdUploadFile className="text-xl" />
                          )}
                        </div>
                        {post.image && (
                          <p className="max-w-full px-2 text-xs font-light text-green-600 break-all">
                            {post.image.name}
                          </p>
                        )}
                        <input
                          type="file"
                          id="banner_input"
                          name="image"
                          data-testid="file-input"
                          accept="image/jpeg, image/png"
                          style={{ display: "none" }}
                          onChange={handleImageUpload}
                        />
                        <div className="text-xs text-center lg:text-xs">
                          <p className="">
                            <label
                              htmlFor="banner_input"
                              className="font-bold text-sm cursor-pointer"
                            >
                              Click Here
                            </label>{" "}
                            &nbsp;
                            <span>to upload your file or drag</span>
                          </p>
                          <p className="text-xs text-gray-500 lg:text-xs">
                            Supported Formats: JPG, PNG, JPEG (10mb each)
                          </p>
                        </div>
                      </div>
                    </div>
                    {errors?.image && (
                      <span className="block mt-2 text-xs text-red-500 lg:text-center">
                        {errors?.image}
                      </span>
                    )}

                    {/* Title */}
                    <input
                      type="text"
                      id="title"
                      name="title"
                      data-testid="title-input"
                      value={post.title}
                      onChange={handleInputChange("title")}
                      className="w-full px-4 py-3 mt-5 mb-1 text-lg border-b border-gray-300 rounded focus:outline-none"
                      placeholder="Title"
                    />
                    {errors?.title && (
                      <span className="text-xs text-red-500">
                        {errors?.title}
                      </span>
                    )}

                    {/* Title Input */}
                    <label
                      htmlFor="description"
                      className="flex justify-end text-base text-[#979797] mb-2 italic"
                    >
                      Max 80 Characters
                    </label>

                    {/* Description */}
                    <input
                      type="text"
                      id="description"
                      name="description"
                      value={post.description}
                      onChange={handleInputChange("description")}
                      className="w-full mb-1 px-4 py-3 bg-[#FBFBFB] text-sm rounded border-none focus:outline-none"
                      placeholder="Write here a short description"
                    />
                    {errors?.description && (
                      <span className="text-xs text-red-500">
                        {errors?.description}
                      </span>
                    )}

                    {/* Description Input */}
                    <label
                      htmlFor="description"
                      className="flex justify-end text-sm text-[#979797] mb-2 italic"
                    >
                      Max 4000 Characters
                    </label>

                    {/* Editor */}
                    <Editor
                      data-testid="content-editor"
                      onChange={handleEditorContentChange}
                    />
                    {errors?.body && (
                      <span className="text-xs text-red-500">
                        {errors?.body}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-center lg:justify-start">
                  <button
                    type="submit"
                    aria-label="Submit blog post"
                    className="px-8 py-3 my-5 text-white bg-black rounded w-44"
                  >
                    {submitted ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      "Publish post"
                    )}
                  </button>
                </div>
              </div>

              {/* Right side - Publishing*/}
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="p-4 bg-white border border-gray-300 rounded-xl">
                  <h3 className="mb-4 text-sm font-bold">PUBLISH</h3>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="category"
                        className="block mb-2 text-sm font-medium"
                      >
                        Select blog category
                      </label>
                      <select
                        data-testid="category-select"
                        className="w-full p-2.5 my-1 text-sm border border-gray-300 rounded-lg focus:outline-none"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                      >
                        <option className="text-sm" value="">
                          Select blog Category
                        </option>
                        {industries &&
                          industries?.map((item, index) => (
                            <option key={item._id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                      </select>
                      {errors?.category && (
                        <span className="text-xs text-red-500">
                          {errors?.category}
                        </span>
                      )}
                    </div>

                    <div className="px-5 py-3 border border-gray-300 rounded-xl">
                      {selectedTags.length >= 1 && (
                        <>
                          <label htmlFor="tags" className="block mb-2 text-sm">
                            Selected Tags
                          </label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {selectedTags.map((tag, index) => (
                              <span
                                key={tag.id || index}
                                className="px-2 py-1 bg-[#ECECEC] rounded text-sm flex items-center gap-2"
                              >
                                {tag}{" "}
                                <button
                                  type="button"
                                  onClick={() => removeTag(tag)}
                                  className="text-lg text-gray-500"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </>
                      )}

                      <label
                        htmlFor="addTag"
                        className="block mt-3 mb-2 text-xs"
                      >
                        Add a tag
                      </label>
                      <div className="flex items-center gap-3 text-sm font-medium">
                        <select
                          data-testid="tags-select"
                          className="w-full p-2.5 my-1 border border-gray-300 rounded"
                          multiple
                          value={currentTag}
                          onChange={(e) => {
                            const selectedOptions = Array.from(
                              e.target.selectedOptions,
                              (option) => option.value
                            );
                            setCurrentTag(selectedOptions);
                          }}
                        >
                          <option value="">Select a tag</option>
                          {availableTags.length > 0 ? (
                            availableTags.map((tag, index) => (
                              <option key={tag.id || index} value={tag}>
                                {tag}
                              </option>
                            ))
                          ) : (
                            <option disabled>No tags available</option>
                          )}
                        </select>
                        <button
                          type="button"
                          disabled={!currentTag || currentTag.length === 0}
                          onClick={() => {
                            if (currentTag && currentTag.length > 0) {
                              setSelectedTags((prevTags) => [
                                ...prevTags,
                                ...currentTag,
                              ]);
                              setCurrentTag([]);
                            }
                          }}
                          className="bg-[#ECECEC] text-[#797979] text-xs px-4 py-2 rounded cursor-pointer"
                        >
                          Add
                        </button>
                      </div>
                      {errors?.tags && (
                        <span className="text-xs text-red-500">
                          {errors?.tags}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={post.featured}
                        onChange={handleInputChange("featured")}
                        className="w-4 h-4 accent-black"
                      />
                      <label htmlFor="featured">Mark post as featured</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50">
            <div className="relative bg-white p-8 rounded-lg w-[90%] max-w-md">
              <button
                className="absolute top-4 right-4 bg-[#DFE1E7] font-extralight text-[#222222] text-base w-8 h-8 rounded-full"
                onClick={closeModal}
              >
                X
              </button>
              <h2 className="mb-2 text-xl font-light">
                Congratulations your blog is successfully uploaded.
              </h2>
              <p className="text-sm text-[#B2B2B2] mb-4">
                You can manage your blogs in the Manage my blogs section in the
                dashboard.
              </p>
              <div className="flex space-x-4">
                <button
                  type="button"
                  className="py-2.5 px-6 text-[13px] bg-black text-white flex items-center rounded-lg"
                  onClick={viewBlogs}
                >
                  View Blogs
                  <IoIosArrowRoundForward className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default PostBlogs;
